import { assert } from '@syncot/util';
import { EventEmitter } from 'events';
/**
 * Strongly-typed nodejs `EventEmitter`.
 *
 * `ListenEvents` defines events that can be listened for, and `EmitEvents` defines those
 * that can be emitted - they will usually be the same. The events are defined as a record:
 *
 * ```
 * {
 *   // An event that requires no parameters.
 *   eventName1: void,
 *   // An event that requires one parameter.
 *   eventName2: Error,
 *   // An event that requires 3 parameters, defined as function parameters.
 *   // This form is the most generic and can express any parameter types.
 *   eventName3: (a: string, b: number, c: { hello: string }) => void
 * }
 * ```
 */
const _TypedEventEmitter = EventEmitter;
export class TypedEventEmitter extends _TypedEventEmitter {
}
/**
 * A strongly-typed event emitter based on nodejs `EventEmitter`.
 * It allows the emitter to be destroyed, in which case events are no
 * longer emitted, unless forced. Additionally, it supports asynchronous
 * event dispatching.
 *
 * The `Events` type param defines the events that can be emitted and
 * listened for. The format is the same as for `TypedEventEmitter`.
 *
 * @emits error Emitted asynchronously when `destroy` is called with an error argument.
 * @emits destroy Emitted asynchronously when this emitter is destroyed.
 */
export class SyncOtEmitter extends TypedEventEmitter {
    constructor() {
        super();
        this._destroyed = false;
    }
    /**
     * Returns true, if this object has been already destroyed, otherwise false.
     */
    get destroyed() {
        return this._destroyed;
    }
    emit(event, ...args) {
        return this._destroyed
            ? this.listenerCount(event) > 0
            : super.emit(event, ...args);
    }
    emitForce(event, ...args) {
        return super.emit(event, ...args);
    }
    emitAsync(name, ...args) {
        process.nextTick(() => this.emit(name, ...args));
    }
    emitAsyncForce(name, ...args) {
        process.nextTick(() => this.emitForce(name, ...args));
    }
    /**
     * Destroys this event emitter and emits a destroy event asynchronously.
     * If an error is specified, it is emitted as an "error" event before the "destroy" event.
     * Does nothing, if the object is already destroyed.
     */
    destroy(error) {
        if (!this._destroyed) {
            this._destroyed = true;
            if (error) {
                this.emitAsyncForce('error', error);
            }
            this.emitAsyncForce('destroy');
        }
    }
    assertNotDestroyed() {
        assert(!this._destroyed, 'Already destroyed.');
    }
}
